import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const inp = { padding: '9px 12px', borderRadius: '6px', border: '1px solid #dee2e6', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' };
const lbl = { display: 'block', marginBottom: '5px', fontSize: '0.8rem', color: '#555', fontWeight: 'bold' };

export default function NotesSection({ patientId, API_BASE }) {
  const [notes, setNotes] = useState([]);
  const [mode, setMode] = useState('list'); // 'list' | 'manual' | 'voice'
  const [manualForm, setManualForm] = useState({ title: '', text: '' });
  const [saving, setSaving] = useState(false);
  const [expandedNote, setExpandedNote] = useState(null);

  // Voice note states
  const [voiceTab, setVoiceTab] = useState('full'); // 'full' | 'medical'
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [medicalText, setMedicalText] = useState('');
  const [voiceTitle, setVoiceTitle] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [voiceSaved, setVoiceSaved] = useState(false);

  const recognitionRef = useRef(null);
  const transcriptRef = useRef('');

  const fetchNotes = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/notes/patient/${patientId}`);
      setNotes(res.data);
    } catch (e) {}
  }, [patientId, API_BASE]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  // ── Manual Note Save ──
  const handleSaveManual = async () => {
    if (!manualForm.text.trim()) return alert('Enter a note!');
    setSaving(true);
    try {
      await axios.post(`${API_BASE}/notes`, {
        patientId,
        title: manualForm.title || 'Manual Note',
        text: manualForm.text,
        type: 'manual'
      });
      setManualForm({ title: '', text: '' });
      setMode('list');
      fetchNotes();
    } catch (e) { alert('Save failed'); }
    setSaving(false);
  };

  // ── Voice Recognition (improved — accumulates final results, never loses words) ──
  const finalTranscriptRef = useRef('');  // all confirmed final results
  const isRecordingRef = useRef(false);

  const startRecording = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Voice recognition not supported. Use Chrome browser.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'hi-IN';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    // Keep what's already in the transcript
    finalTranscriptRef.current = transcript;

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          // Final result — append permanently
          finalTranscriptRef.current += text + ' ';
        } else {
          // Interim — show as preview (grey text)
          interim += text;
        }
      }
      // Show final + current interim
      setTranscript(finalTranscriptRef.current + interim);
    };

    recognition.onerror = (e) => {
      console.error('Speech error:', e.error);
      // Don't stop on no-speech or network errors — just restart
      if (e.error === 'aborted' || e.error === 'not-allowed') {
        isRecordingRef.current = false;
        setIsRecording(false);
      }
    };

    recognition.onend = () => {
      // Auto-restart if still recording (handles Chrome's ~60s auto-stop)
      if (isRecordingRef.current) {
        try { recognition.start(); } catch(e) {
          // Small delay before retry
          setTimeout(() => {
            if (isRecordingRef.current) {
              try { recognition.start(); } catch(e2) {}
            }
          }, 200);
        }
      }
    };

    recognitionRef.current = recognition;
    isRecordingRef.current = true;
    recognition.start();
    setIsRecording(true);
    setVoiceSaved(false);
    setMedicalText('');
  };

  const stopRecording = () => {
    isRecordingRef.current = false;
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsRecording(false);
    // Finalize transcript
    setTranscript(finalTranscriptRef.current);
  };

  const handleExtractMedical = async () => {
    if (!transcript.trim()) return alert('Record a conversation first!');
    setExtracting(true);
    try {
      const res = await axios.post(`${API_BASE}/notes/extract-medical`, { text: transcript });
      setMedicalText(res.data.medicalText || '');
      setVoiceTab('medical');
    } catch (e) { alert('Medical text extraction failed'); }
    setExtracting(false);
  };

  const handleSaveVoiceNote = async () => {
    if (!transcript.trim()) return alert('No transcript available!');
    setSaving(true);
    try {
      await axios.post(`${API_BASE}/notes`, {
        patientId,
        title: voiceTitle || `Voice Note — ${new Date().toLocaleDateString('en-IN')}`,
        text: transcript,
        medicalText: medicalText,
        type: 'voice'
      });
      setVoiceSaved(true);
      setTranscript('');
      setMedicalText('');
      setVoiceTitle('');
      setMode('list');
      fetchNotes();
    } catch (e) { alert('Save failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    try { await axios.delete(`${API_BASE}/notes/${id}`); fetchNotes(); } catch (e) {}
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
        <h3 style={{ margin: 0, color: '#003554' }}>📝 Patient Notes ({notes.length})</h3>
        {mode === 'list' && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => setMode('manual')}
              style={{ padding: '8px 18px', backgroundColor: '#006494', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
              ✏️ Manual Note
            </button>
            <button onClick={() => { setMode('voice'); setTranscript(''); setMedicalText(''); setVoiceSaved(false); }}
              style={{ padding: '8px 18px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
              🎙️ Voice Note (AI)
            </button>
          </div>
        )}
        {mode !== 'list' && (
          <button onClick={() => { setMode('list'); stopRecording(); }}
            style={{ padding: '8px 16px', border: '1px solid #dee2e6', borderRadius: '8px', background: 'white', cursor: 'pointer', color: '#555', fontWeight: 'bold' }}>
            ← Back
          </button>
        )}
      </div>

      {/* ── MANUAL NOTE FORM ── */}
      {mode === 'manual' && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', border: '2px solid #006494', marginBottom: '16px' }}>
          <h4 style={{ margin: '0 0 16px', color: '#006494' }}>✏️ New Manual Note</h4>
          <div style={{ marginBottom: '12px' }}>
            <label style={lbl}>Title</label>
            <input placeholder="Note ka title..." value={manualForm.title}
              onChange={e => setManualForm(f => ({ ...f, title: e.target.value }))} style={inp} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={lbl}>Note *</label>
            <textarea rows={6} placeholder="Write notes here... observations, next visit plan, etc."
              value={manualForm.text} onChange={e => setManualForm(f => ({ ...f, text: e.target.value }))}
              style={{ ...inp, resize: 'vertical', lineHeight: '1.5' }} />
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={() => setMode('list')}
              style={{ padding: '9px 20px', border: '1px solid #dee2e6', borderRadius: '7px', background: 'white', cursor: 'pointer', color: '#555', fontWeight: 'bold' }}>Cancel</button>
            <button onClick={handleSaveManual} disabled={saving}
              style={{ padding: '9px 24px', backgroundColor: '#006494', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontWeight: 'bold' }}>
              {saving ? 'Saving...' : '💾 Save Note'}
            </button>
          </div>
        </div>
      )}

      {/* ── VOICE NOTE FORM ── */}
      {mode === 'voice' && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', border: '2px solid #dc3545', marginBottom: '16px' }}>
          <h4 style={{ margin: '0 0 6px', color: '#dc3545' }}>🎙️ Voice Note — AI Powered</h4>
          <p style={{ fontSize: '0.82rem', color: '#888', margin: '0 0 16px' }}>
            Hindi / Rajasthani mein doctor-patient conversation record karein. AI do notes banayega: ek full transcript, ek sirf medical content.
          </p>

          <div style={{ marginBottom: '12px' }}>
            <label style={lbl}>Note Title</label>
            <input placeholder={`Voice Note — ${new Date().toLocaleDateString('en-IN')}`}
              value={voiceTitle} onChange={e => setVoiceTitle(e.target.value)} style={inp} />
          </div>

          {/* Recording controls */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', padding: '14px', backgroundColor: '#fff5f5', borderRadius: '10px', border: '1px solid #f5c6cb' }}>
            {!isRecording ? (
              <button onClick={startRecording}
                style={{ padding: '12px 24px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🎙️ Start Recording
              </button>
            ) : (
              <button onClick={stopRecording}
                style={{ padding: '12px 24px', backgroundColor: '#c82333', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px', animation: 'pulse 1s infinite' }}>
                ⏹ Stop Recording
              </button>
            )}
            {isRecording && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '12px', height: '12px', backgroundColor: '#dc3545', borderRadius: '50%', animation: 'blink 1s step-end infinite' }} />
                <span style={{ color: '#dc3545', fontWeight: 'bold', fontSize: '0.9rem' }}>Recording... Hindi/Rajasthani mein bolein</span>
              </div>
            )}
          </div>

          <style>{`@keyframes blink { 50%{opacity:0} } @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.7} }`}</style>

          {/* Transcript tabs */}
          <div style={{ marginBottom: '8px', display: 'flex', gap: '4px', borderBottom: '2px solid #e9ecef' }}>
            <button onClick={() => setVoiceTab('full')}
              style={{ padding: '8px 18px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.88rem',
                backgroundColor: 'transparent', color: voiceTab === 'full' ? '#dc3545' : '#888',
                borderBottom: voiceTab === 'full' ? '3px solid #dc3545' : '3px solid transparent', borderRadius: 0, marginBottom: '-2px' }}>
              📝 Full Transcript
            </button>
            <button onClick={() => setVoiceTab('medical')}
              style={{ padding: '8px 18px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.88rem',
                backgroundColor: 'transparent', color: voiceTab === 'medical' ? '#006494' : '#888',
                borderBottom: voiceTab === 'medical' ? '3px solid #006494' : '3px solid transparent', borderRadius: 0, marginBottom: '-2px' }}>
              🏥 Medical Only (AI)
            </button>
          </div>

          {voiceTab === 'full' && (
            <div>
              <textarea rows={7} placeholder="Recording ke baad transcript yahan dikhegi..." value={transcript}
                onChange={e => setTranscript(e.target.value)}
                style={{ ...inp, resize: 'vertical', lineHeight: '1.6', backgroundColor: transcript ? 'white' : '#f8f9fa', marginBottom: '10px' }} />
              <button onClick={handleExtractMedical} disabled={!transcript.trim() || extracting}
                style={{ padding: '9px 20px', backgroundColor: '#006494', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', opacity: (!transcript.trim() || extracting) ? 0.6 : 1 }}>
                {extracting ? '⏳ Extracting...' : '🤖 Extract Medical Content (AI)'}
              </button>
            </div>
          )}

          {voiceTab === 'medical' && (
            <div>
              {!medicalText ? (
                <div style={{ padding: '30px', textAlign: 'center', color: '#aaa', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  Pehle "Extract Medical Content" button dabayein
                </div>
              ) : (
                <textarea rows={7} value={medicalText} onChange={e => setMedicalText(e.target.value)}
                  style={{ ...inp, resize: 'vertical', lineHeight: '1.6', backgroundColor: '#f0fff4' }} />
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '14px' }}>
            <button onClick={() => { setMode('list'); stopRecording(); }}
              style={{ padding: '9px 20px', border: '1px solid #dee2e6', borderRadius: '7px', background: 'white', cursor: 'pointer', color: '#555', fontWeight: 'bold' }}>Cancel</button>
            <button onClick={handleSaveVoiceNote} disabled={!transcript.trim() || saving}
              style={{ padding: '9px 24px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontWeight: 'bold', opacity: (!transcript.trim() || saving) ? 0.6 : 1 }}>
              {saving ? 'Saving...' : '💾 Save Voice Note'}
            </button>
          </div>
        </div>
      )}

      {/* ── NOTES LIST ── */}
      {mode === 'list' && (
        <div>
          {notes.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#bbb', padding: '50px', backgroundColor: '#fafafa', borderRadius: '10px' }}>
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📝</div>
              <div>No notes yet — add one above</div>
            </div>
          ) : (
            notes.map(note => (
              <div key={note._id} style={{ backgroundColor: 'white', borderRadius: '10px', padding: '16px', marginBottom: '12px', border: `1px solid ${note.type === 'voice' ? '#f5c6cb' : '#dee2e6'}`, boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '1rem' }}>{note.type === 'voice' ? '🎙️' : '✏️'}</span>
                      <span style={{ fontWeight: 'bold', color: '#003554', fontSize: '0.95rem' }}>{note.title}</span>
                      <span style={{ fontSize: '0.72rem', backgroundColor: note.type === 'voice' ? '#fff5f5' : '#f0f9ff', color: note.type === 'voice' ? '#dc3545' : '#006494', padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold', border: `1px solid ${note.type === 'voice' ? '#f5c6cb' : '#bee3f8'}` }}>
                        {note.type === 'voice' ? 'Voice' : 'Manual'}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#aaa', marginBottom: '8px' }}>
                      {formatDate(note.date || note.createdAt)}
                    </div>

                    {/* Preview / expanded */}
                    {expandedNote === note._id ? (
                      <div>
                        {note.type === 'voice' && note.medicalText ? (
                          <div>
                            <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#888', textTransform: 'uppercase', marginBottom: '4px' }}>📝 Full Transcript</div>
                            <div style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '10px 12px', fontSize: '0.87rem', lineHeight: '1.6', marginBottom: '10px', whiteSpace: 'pre-wrap' }}>{note.text}</div>
                            <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#888', textTransform: 'uppercase', marginBottom: '4px' }}>🏥 Medical Content Only</div>
                            <div style={{ backgroundColor: '#f0fff4', borderRadius: '8px', padding: '10px 12px', fontSize: '0.87rem', lineHeight: '1.6', whiteSpace: 'pre-wrap', border: '1px solid #c3e6cb' }}>{note.medicalText}</div>
                          </div>
                        ) : (
                          <div style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '10px 12px', fontSize: '0.87rem', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{note.text}</div>
                        )}
                      </div>
                    ) : (
                      <div style={{ color: '#555', fontSize: '0.88rem', lineHeight: '1.5' }}>
                        {note.text?.length > 120 ? note.text.substring(0, 120) + '...' : note.text}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginLeft: '12px', flexShrink: 0 }}>
                    <button onClick={() => setExpandedNote(expandedNote === note._id ? null : note._id)}
                      style={{ padding: '5px 12px', backgroundColor: '#e9ecef', color: '#333', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 'bold' }}>
                      {expandedNote === note._id ? 'Collapse' : 'View'}
                    </button>
                    <button onClick={() => handleDelete(note._id)}
                      style={{ padding: '5px 12px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 'bold' }}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
